import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { showToast } from '../lib/toast';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { CustomerPortalLinkedArticlesResponse } from '../types/articles';
import type { CustomerTicketScope } from '../types/tickets';

// Customer portal ticket filter parameters
export interface CustomerTicketFilterParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: number[];
  priority?: number[];
  sortOrder?: 'asc' | 'desc';
  scope?: CustomerTicketScope;  // 'mine' (default) or 'participating' - tickets the customer is a participant on
}

// Customer portal specific query keys - exported for use in SignalR handlers
export const customerPortalQueryKeys = {
  tickets: {
    all: ['customer-portal', 'tickets'] as const,
    lists: () => [...customerPortalQueryKeys.tickets.all, 'list'] as const,
    list: (filter: CustomerTicketFilterParams) => [...customerPortalQueryKeys.tickets.lists(), filter] as const,
    details: () => [...customerPortalQueryKeys.tickets.all, 'detail'] as const,
    detail: (id: string) => [...customerPortalQueryKeys.tickets.details(), id] as const,
    stats: () => [...customerPortalQueryKeys.tickets.all, 'stats'] as const,
    insights: () => [...customerPortalQueryKeys.tickets.all, 'insights'] as const,
    activity: () => [...customerPortalQueryKeys.tickets.all, 'activity'] as const,
    articleLinks: (ticketId: string) => [...customerPortalQueryKeys.tickets.detail(ticketId), 'articleLinks'] as const,
  },
  categories: {
    all: ['customer-portal', 'categories'] as const,
    suggestedArticles: (categoryId: string) => ['customer-portal', 'categories', categoryId, 'suggested-articles'] as const,
  },
  lookups: {
    all: ['customer-portal', 'lookups'] as const,
  },
};

// Get customer tickets with server-side filtering and pagination
export function useCustomerTickets(filter: CustomerTicketFilterParams = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: customerPortalQueryKeys.tickets.list(filter),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalTickets({
        page: filter.page,
        pageSize: filter.pageSize,
        search: filter.search,
        status: filter.status,
        priority: filter.priority,
        sortOrder: filter.sortOrder,
        scope: filter.scope,
      });
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch tickets'));
      }
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Get customer ticket statistics
export function useCustomerTicketStats() {
  return useQuery({
    queryKey: customerPortalQueryKeys.tickets.stats(),
    queryFn: async () => {
      const response = await apiService.getCustomerTicketStats();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket statistics'));
      }
      return response.data;
    },
  });
}

// Get customer insights for dashboard charts
export function useCustomerInsights() {
  return useQuery({
    queryKey: customerPortalQueryKeys.tickets.insights(),
    queryFn: async () => {
      const response = await apiService.getCustomerInsights();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch insights'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Get customer recent activity for dashboard
export function useCustomerRecentActivity() {
  return useQuery({
    queryKey: customerPortalQueryKeys.tickets.activity(),
    queryFn: async () => {
      const response = await apiService.getCustomerRecentActivity();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch recent activity'));
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes - activity changes more frequently
  });
}

// Get single customer ticket
export function useCustomerTicket(id: string) {
  return useQuery({
    queryKey: customerPortalQueryKeys.tickets.detail(id),
    queryFn: async () => {
      const response = await apiService.getCustomerTicket(id);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket'));
      }
      return response.data;
    },
    enabled: !!id,
  });
}

// Create customer ticket mutation using submitCustomerTicket for JSON data
export function useCreateCustomerTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ticketData: { subject: string; description: string; priority: number; categoryId?: string }) => {
      const response = await apiService.submitCustomerTicket(ticketData);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to create ticket'));
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate customer tickets list
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
    },
  });
}

// Add customer comment mutation
export function useAddCustomerComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, content, attachments }: { ticketId: string; content: string; attachments?: File[] }) => {
      const response = await apiService.addCustomerComment(ticketId, content, attachments);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to add comment'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate the ticket details to refetch with new comment
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
      showToast.success('Success', 'Comment added successfully');
    },
    onError: (error: Error) => {
      showToast.error('Failed to Add Comment', error.message);
    },
  });
}

// Update customer ticket mutation (only priority and category)
export function useUpdateCustomerTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, updates }: { ticketId: string; updates: { priority?: number; categoryId?: string } }) => {
      const response = await apiService.updateCustomerTicket(ticketId, updates);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update ticket'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate both ticket details and ticket lists
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
      showToast.success('Success', 'Ticket updated successfully');
    },
    onError: (error: Error) => {
      showToast.error('Failed to Update Ticket', error.message);
    },
  });
}

// Upload customer comment attachment mutation
export function useUploadCustomerCommentAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, commentId, file }: { ticketId: string; commentId: string; file: File }) => {
      const response = await apiService.uploadCustomerCommentAttachment(ticketId, commentId, file);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to upload attachment'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate the ticket details to refetch with new attachment
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
    },
  });
}

// Get customer portal categories
export function useCustomerPortalCategories() {
  return useQuery({
    queryKey: customerPortalQueryKeys.categories.all,
    queryFn: async () => {
      const response = await apiService.getCustomerPortalCategories();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch categories'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Categories don't change often, cache for 5 minutes
  });
}

// Knowledge base articles that might answer a question in this category, for the new-ticket form
export function useCustomerPortalSuggestedArticles(categoryId: string | undefined) {
  return useQuery({
    queryKey: customerPortalQueryKeys.categories.suggestedArticles(categoryId ?? ''),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalSuggestedArticles(categoryId!);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch suggested articles'));
      }
      return response.data ?? [];
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });
}

// Get customer portal lookups (priorities, statuses)
export function useCustomerPortalLookups() {
  return useQuery({
    queryKey: customerPortalQueryKeys.lookups.all,
    queryFn: async () => {
      const response = await apiService.getCustomerPortalLookups();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch lookups'));
      }
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // Lookups are very stable, cache for 10 minutes
  });
}

// Get available actions for a ticket
export function useCustomerTicketActions(ticketId: string) {
  return useQuery({
    queryKey: [...customerPortalQueryKeys.tickets.detail(ticketId), 'actions'],
    queryFn: async () => {
      const response = await apiService.getCustomerTicketActions(ticketId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket actions'));
      }
      return response.data;
    },
    enabled: !!ticketId,
    staleTime: 30 * 1000, // Refresh actions every 30 seconds
  });
}

// Resolve ticket mutation
export function useResolveCustomerTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, comment }: { ticketId: string; comment?: string }) => {
      const response = await apiService.resolveCustomerTicket(ticketId, comment);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to resolve ticket'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate ticket details, lists, and actions
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: [...customerPortalQueryKeys.tickets.detail(ticketId), 'actions'] });
      showToast.success('Success', 'Ticket marked as resolved');
    },
    onError: (error: Error) => {
      showToast.error('Failed to Resolve Ticket', error.message);
    },
  });
}

// Reopen ticket mutation
export function useReopenCustomerTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, comment }: { ticketId: string; comment?: string }) => {
      const response = await apiService.reopenCustomerTicket(ticketId, comment);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to reopen ticket'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate ticket details, lists, and actions
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: [...customerPortalQueryKeys.tickets.detail(ticketId), 'actions'] });
      showToast.success('Success', 'Ticket reopened');
    },
    onError: (error: Error) => {
      showToast.error('Failed to Reopen Ticket', error.message);
    },
  });
}

export function useCustomerPortalTicketArticles(ticketId: string | undefined) {
  return useQuery({
    queryKey: customerPortalQueryKeys.tickets.articleLinks(ticketId!),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalTicketArticles(ticketId!);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch linked articles'));
      }
      return response.data as CustomerPortalLinkedArticlesResponse;
    },
    enabled: !!ticketId,
    staleTime: 60000,
  });
}